import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CreditCard, LogIn, Phone, ShieldCheck, Smartphone, Wrench } from 'lucide-react';
import { useAuthStore } from '@/store/useAuthStore';
import { AuthModal } from '@/components/AuthModal';
import { PaymentMethod, PaymentStatus } from '../types';
import { PAYMENT_METHOD_META } from '../config/defaults';
import { usePaymentStore } from '../state/usePaymentStore';
import { usePaymentGate } from '../hooks/usePaymentGate';
import { usePaymentFlow } from '../hooks/usePaymentFlow';
import { usePaymentService } from '../hooks/usePaymentService';
import { PaymentStatusView } from '../components/PaymentStatusView';
import { formatPrice } from '../components/PlanCard';

/** Icône par moyen de paiement (les libellés/hints viennent de PAYMENT_METHOD_META). */
const METHOD_ICON: Record<PaymentMethod, JSX.Element> = {
  [PaymentMethod.BANK_CARD]: <CreditCard className="w-5 h-5" />,
  [PaymentMethod.MOBILE_MONEY]: <Smartphone className="w-5 h-5" />,
  [PaymentMethod.ORANGE_MONEY]: <Smartphone className="w-5 h-5" />,
  [PaymentMethod.MPESA]: <Smartphone className="w-5 h-5" />,
};

/**
 * ─── Écran : Paiement (checkout) ─────────────────────────────────────────────
 * Récapitulatif + choix du moyen de paiement. Le provider est résolu
 * automatiquement par PaymentService (canal + config admin) — l'utilisateur
 * ne choisit jamais "FlexPay" ou "M-Pesa", seulement carte / téléphone.
 */
export function CheckoutPage() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const service = usePaymentService();
  const { paymentsEnabled, canPurchaseInApp } = usePaymentGate();
  const config = usePaymentStore((s) => s.config);
  const plan = usePaymentStore((s) => s.plans.find((p) => p.id === planId));
  const { attempt, startPayment, retry, reset, verifyAgain } = usePaymentFlow();

  // Options du canal courant (Maxit / M-Pesa / web), filtrées par les
  // kill-switches admin. Recalculé si la config change (activation/maintenance).
  const options = useMemo(() => service.getAvailableOptions(), [service, config]);
  const availableMethods = options.map((o) => o.method);
  const [method, setMethod] = useState<PaymentMethod | null>(
    availableMethods[0] ?? null,
  );
  // La méthode sélectionnée n'est plus disponible (coupée entre-temps) →
  // retomber sur la première option restante.
  const selectedMethod =
    method && availableMethods.includes(method) ? method : availableMethods[0] ?? null;

  // Les moyens mobile (money / Orange / M-Pesa) exigent le numéro du payeur
  // (push validé par PIN). La carte bancaire n'en a pas besoin.
  const [phone, setPhone] = useState('');
  const needsPhone =
    selectedMethod !== null && selectedMethod !== PaymentMethod.BANK_CARD;
  const phoneDigits = phone.replace(/\D/g, '');
  const phoneValid = phoneDigits.length >= 9 && phoneDigits.length <= 13;
  const canPay = selectedMethod !== null && (!needsPhone || phoneValid);

  // Payer exige un COMPTE (pas une session invitée) : l'abonnement est
  // rattaché au compte et la facture envoyée par email — il ne se perd jamais.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isGuestSession = useAuthStore((s) => s.isGuestSession);
  const [showAuth, setShowAuth] = useState(false);
  const requiresAccount = !isAuthenticated || isGuestSession;

  if (!paymentsEnabled || !canPurchaseInApp || config.maintenanceMode) return <Navigate to="/plans" replace />;
  if (!plan) return <Navigate to="/plans" replace />;

  if (requiresAccount) {
    return (
      <div className="min-h-screen bg-app px-4 py-8">
        <div className="max-w-md mx-auto">
          <button
            onClick={() => navigate('/plans')}
            className="inline-flex items-center gap-1.5 text-xs text-fg-faint hover:text-fg-subtle transition-colors mb-6"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Changer de plan
          </button>
          <div className="bg-overlay-1 border border-line rounded-2xl p-8 text-center">
            <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-lg">
              <LogIn className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-lg font-bold text-fg mb-2">Connectez-vous pour vous abonner</h1>
            <p className="text-sm text-fg-subtle mb-6 leading-relaxed">
              Votre abonnement est rattaché à votre compte : vous le retrouvez sur
              tous vos appareils et recevez vos factures par email.
            </p>
            <button
              onClick={() => setShowAuth(true)}
              className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl text-sm font-semibold active:scale-[0.98] transition-all"
            >
              Se connecter ou créer un compte
            </button>
          </div>
        </div>
        <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
      </div>
    );
  }

  // Une tentative est en cours pour CE plan → afficher son état plutôt que le
  // formulaire (survit aux rechargements grâce au store persisté).
  const showStatus =
    attempt && attempt.planId === plan.id && attempt.status !== PaymentStatus.IDLE;

  return (
    <div className="min-h-screen bg-app px-4 py-8">
      <div className="max-w-md mx-auto">
        <button
          onClick={() => { reset(); navigate('/plans'); }}
          className="inline-flex items-center gap-1.5 text-xs text-fg-faint hover:text-fg-subtle transition-colors mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Changer de plan
        </button>

        {showStatus && attempt ? (
          <PaymentStatusView
            status={attempt.status}
            detail={attempt.error}
            actions={[
              ...(attempt.status === PaymentStatus.FAILED ||
              attempt.status === PaymentStatus.CANCELLED ||
              attempt.status === PaymentStatus.NETWORK_ERROR
                ? [{ label: 'Réessayer le paiement', onClick: () => void retry() }]
                : []),
              ...(attempt.status === PaymentStatus.EXPIRED ||
              attempt.status === PaymentStatus.NETWORK_ERROR
                ? [{ label: 'Re-vérifier le paiement', onClick: verifyAgain, variant: 'secondary' as const }]
                : []),
              ...(attempt.status === PaymentStatus.SUCCESS
                ? [{ label: 'Continuer', onClick: () => navigate('/subscription') }]
                : []),
            ]}
          />
        ) : (
          <div className="bg-overlay-1 border border-line rounded-2xl p-6">
            <h1 className="text-lg font-bold text-fg mb-5">Récapitulatif</h1>

            <div className="flex items-center justify-between py-3 border-b border-line">
              <div>
                <p className="text-sm font-semibold text-fg">Plan {plan.name}</p>
                <p className="text-xs text-fg-faint">Abonnement mensuel · {plan.periodDays} jours</p>
              </div>
              <p className="text-lg font-bold text-fg">
                {formatPrice(plan.price, plan.currency)}
              </p>
            </div>

            <p className="text-xs font-medium text-fg-subtle uppercase tracking-wide mt-5 mb-3">
              Moyen de paiement
            </p>

            {availableMethods.length === 0 ? (
              // Aucun moyen de paiement actif sur ce canal (tous coupés pour
              // maintenance, ou canal Maxit dont l'Orange Money est suspendu).
              <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-5 text-center">
                <Wrench className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                <p className="text-sm font-semibold text-fg">
                  Paiement momentanément indisponible
                </p>
                <p className="text-xs text-fg-subtle mt-1 leading-relaxed">
                  Les moyens de paiement sont en maintenance. Merci de réessayer
                  dans quelques instants.
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {availableMethods.map((m) => (
                    <button
                      key={m}
                      onClick={() => setMethod(m)}
                      className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border text-left transition-colors ${
                        selectedMethod === m
                          ? 'border-green-500/60 bg-green-500/10 text-fg'
                          : 'border-line bg-overlay-2 text-fg-muted hover:bg-overlay-2/70'
                      }`}
                    >
                      {METHOD_ICON[m]}
                      <span className="flex-1">
                        <span className="block text-sm font-semibold">{PAYMENT_METHOD_META[m].label}</span>
                        <span className="block text-xs text-fg-faint">{PAYMENT_METHOD_META[m].hint}</span>
                      </span>
                      <span
                        className={`w-4 h-4 rounded-full border-2 ${
                          selectedMethod === m ? 'border-green-500 bg-green-500' : 'border-line'
                        }`}
                      />
                    </button>
                  ))}
                </div>

                {needsPhone && (
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-fg-subtle mb-1.5">
                      Numéro de téléphone à débiter
                    </label>
                    <div className="flex items-center gap-2 px-4 py-3 rounded-xl border border-line bg-overlay-2 focus-within:border-green-500/60">
                      <Phone className="w-4 h-4 text-fg-faint" />
                      <input
                        type="tel"
                        inputMode="numeric"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Ex. 0814243950"
                        className="flex-1 bg-transparent text-sm text-fg placeholder-fg-faint outline-none"
                      />
                    </div>
                    <p className="text-[11px] text-fg-faint mt-1.5">
                      Vous recevrez une demande de validation sur ce numéro.
                    </p>
                  </div>
                )}

                <button
                  onClick={() =>
                    selectedMethod &&
                    void startPayment(
                      plan,
                      selectedMethod,
                      needsPhone ? phone : undefined,
                    )
                  }
                  disabled={!canPay}
                  className="w-full mt-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3.5 rounded-xl font-semibold text-sm active:scale-[0.98] transition-all disabled:opacity-50"
                >
                  Payer {formatPrice(plan.price, plan.currency)}
                </button>

                <p className="flex items-center justify-center gap-1.5 text-[11px] text-fg-faint mt-4">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Vous serez redirigé vers une page de paiement sécurisée
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
